import { notification } from 'antd';
import React from 'react';

import { apiClient } from '../../graphql/api/apiClient';
import {
  deleteEmergencyScenarioDocument,
  EmergencyScenarioLabelFragment,
  EmergencyScenarioStatus,
} from '../../graphql/api/generated';
import { updateEmergencyScenario } from '../../graphql/api/operations';
import { PermissionKey_enum } from '../../graphql/hasura/generated';
import { history } from '../../routes/history';

import model from './model';

export async function updateEmergencyScenarioStatus(
  emergencyScenarioId: string,
  nextStatus: EmergencyScenarioStatus,
) {
  await updateEmergencyScenario(emergencyScenarioId, { status: nextStatus });

  notification.success({
    message: 'Success',
    description: (
      <>
        The emergency scenario status has been set to <strong>{nextStatus}</strong>
      </>
    ),
  });

  await apiClient.resetStore();

  const redirectPath = model.routes.getRowLinkById(emergencyScenarioId);

  if (redirectPath && location.pathname !== redirectPath) {
    history.push(redirectPath);
  }
}

export const deleteAction = model.createAction<EmergencyScenarioLabelFragment>({
  label: () => 'Delete',
  enabledByModel: ({ hasPermission }) => hasPermission(PermissionKey_enum.EmergencyScenario_Manage),
  enabledByRow: ({ status }) => status === EmergencyScenarioStatus.INACTIVE,
  confirmation: {
    title: () => 'Delete Emergency Scenario',
    content: () => 'Are you sure you want to delete this Emergency Scenario?',
    okType: 'danger',
  },
  executes: async ({ emergencyScenarioId }, { resetStores, location }) => {
    await apiClient.mutate({
      mutation: deleteEmergencyScenarioDocument,
      variables: { emergencyScenarioId },
    });

    await resetStores(); // Reset cache to refetch queries

    const detailsPath = model.routes.getRowLinkById(emergencyScenarioId);

    if (detailsPath && location.pathname === detailsPath) {
      history.push(model.routes.defaultRoute.path);
    }

    notification.success({
      message: 'Success',
      description: 'The emergency scenario was successfully deleted!',
    });
  },
});

export const toggleStatusAction = model.createAction<EmergencyScenarioLabelFragment>({
  label: ({ status }) => status === EmergencyScenarioStatus.INACTIVE ? 'Activate' : 'Deactivate',
  enabledByModel: ({ hasPermission }) => hasPermission(PermissionKey_enum.EmergencyScenario_Manage),
  confirmation: {
    title: ({ status }) => (
      `${status === EmergencyScenarioStatus.INACTIVE ? 'Activate' : 'Deactivate'} Emergency Scenario`
    ),
    content: ({ status }) => (
      `Are you sure you want to ` +
      `${status === EmergencyScenarioStatus.INACTIVE ? 'activate' : 'deactivate'} ` +
      `this Emergency Scenario?`
    ),
    okType: 'danger',
  },
  executes: async ({ emergencyScenarioId, status }) => {
    const nextStatus = status === EmergencyScenarioStatus.INACTIVE
      ? EmergencyScenarioStatus.ACTIVE
      : EmergencyScenarioStatus.INACTIVE;

    await updateEmergencyScenarioStatus(emergencyScenarioId, nextStatus);
  },
});
